import { Invoice, InvoicePayment } from '../../types';
import { openAmountCents, validatePayment } from '../invoiceLogic';
import type { SplitPart } from '../invoicePayments';
import { baseBankTxId } from './bankTxId';
import { centsFromEuroInput, Cents, formatCents } from '../money';
import { checkRemainder, isRemainderMethod, paymentRemainder, RemainderKind } from '../paymentRemainder';
import { BankTransaction } from './camt053';
import { PaymentMatch } from './matchPayments';

/**
 * Regeln des Kontoauszugsimports, die nicht an der Oberflaeche haengen:
 * was schon erfasst ist, und ob eine Aufteilung auf mehrere Rechnungen
 * buchbar ist. Ohne React pruefbar.
 */

const de = (iso: string): string => iso.split('-').reverse().join('.');

const invoiceName = (invoice: Invoice): string =>
    invoice.invoiceNumber ? `Rechnung ${invoice.invoiceNumber}` : 'Die Rechnung ohne Nummer';

// ═══════════════════════════════════════════════════════════════════════════
// Was schon erfasst ist
// ═══════════════════════════════════════════════════════════════════════════

export interface PaymentIndex {
    /** Bankschluessel aller Zahlungen — bei aufgeteilten Zahlungen auch der Umsatzschluessel. */
    importedBankTxIds: Set<string>;
    /** Von Hand erfasste Eingaenge (ohne Bankschluessel, kein Skonto/Einbehalt) je Rechnung. */
    manualByInvoice: Map<string, InvoicePayment[]>;
}

export const emptyPaymentIndex = (): PaymentIndex => ({
    importedBankTxIds: new Set(),
    manualByInvoice: new Map(),
});

export const buildPaymentIndex = (payments: readonly InvoicePayment[]): PaymentIndex => {
    const index = emptyPaymentIndex();
    for (const payment of payments) {
        if (payment.bankTxId) {
            index.importedBankTxIds.add(payment.bankTxId);
            // Ein aufgeteilter Umsatz traegt nur Teilschluessel (#T1, #T2 …);
            // gefragt wird aber nach dem Umsatz selbst.
            index.importedBankTxIds.add(baseBankTxId(payment.bankTxId));
            continue;
        }
        if (payment.direction !== 'INCOMING' || isRemainderMethod(payment.method)) continue;
        const list = index.manualByInvoice.get(payment.invoiceId);
        if (list) list.push(payment);
        else index.manualByInvoice.set(payment.invoiceId, [payment]);
    }
    return index;
};

/** Wie weit Wertstellung und Datum einer Handzahlung auseinander liegen duerfen. */
const MANUAL_MATCH_DAYS = 14;

const daysBetween = (a: string, b: string): number =>
    Math.abs(Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000;

/**
 * Warum ein Umsatz als bereits erfasst gilt — oder null.
 *
 * Bewusst eng, damit nichts Neues verschwindet: gleiche Betraege sind bei
 * diesem Betrieb haeufig (dieselbe Landeskasse zahlt mehrmals 761,60 €), ein
 * Betragsvergleich allein wuerde echte neue Eingaenge ausblenden. Gezaehlt
 * wird deshalb nur:
 *   • der Umsatz steht schon als Zahlung in der Datenbank (Bankschluessel);
 *   • auf der im Verwendungszweck genannten Rechnung steht eine von Hand
 *     erfasste Zahlung ueber genau diesen Betrag, hoechstens 14 Tage neben
 *     der Wertstellung.
 * Eine bloss ausgeglichene Rechnung zaehlt NICHT (Codex Runde 6): zahlt der
 * Kunde ein zweites Mal, muss genau dieser Eingang sichtbar bleiben — der
 * Zuordnungsgrund sagt dann „bereits ausgeglichen, auf Doppelzahlung pruefen".
 */
export const doneReason = (
    match: PaymentMatch,
    index: PaymentIndex,
    /** Handzahlungen, die schon einem anderen Umsatz zugeordnet sind. */
    consumed: Set<string> = new Set(),
): string | null => {
    const tx = match.transaction;
    if (index.importedBankTxIds.has(tx.bankTxId)) {
        return 'Dieser Umsatz ist bereits als Zahlung erfasst.';
    }
    if (tx.amountCents <= 0 || match.rule !== 'invoice_number' || !match.invoice) return null;
    const invoice = match.invoice;
    const manual = (index.manualByInvoice.get(invoice.id) ?? [])
        .filter(
            (payment) =>
                !consumed.has(payment.id) &&
                payment.amountCents === tx.amountCents &&
                daysBetween(payment.valueDate, tx.valueDate) <= MANUAL_MATCH_DAYS,
        )
        .sort(
            (a, b) => daysBetween(a.valueDate, tx.valueDate) - daysBetween(b.valueDate, tx.valueDate),
        )[0];
    if (manual) {
        consumed.add(manual.id);
        return `Auf ${invoiceName(invoice)} steht schon eine von Hand erfasste Zahlung über ${formatCents(
            manual.amountCents,
        )} vom ${de(manual.valueDate)}.`;
    }
    return null;
};

/**
 * doneReason fuer alle Umsaetze einer Einlesung.
 *
 * Eine Handzahlung erklaert hoechstens EINEN Umsatz (Codex Runde 7): zahlt
 * der Kunde denselben Betrag ein zweites Mal, bleibt der zweite Eingang
 * sichtbar. Umsaetze, die naeher am Datum der Handzahlung liegen, kommen
 * zuerst zum Zug.
 */
export const doneReasons = (matches: readonly PaymentMatch[], index: PaymentIndex): Array<string | null> => {
    const consumed = new Set<string>();
    const result: Array<string | null> = matches.map(() => null);
    const order = matches
        .map((match, position) => ({ match, position }))
        .sort((a, b) => (a.match.transaction.valueDate < b.match.transaction.valueDate ? -1 : a.match.transaction.valueDate > b.match.transaction.valueDate ? 1 : a.position - b.position));
    for (const { match, position } of order) {
        result[position] = doneReason(match, index, consumed);
    }
    return result;
};

// ═══════════════════════════════════════════════════════════════════════════
// Aufteilung auf mehrere Rechnungen
// ═══════════════════════════════════════════════════════════════════════════

export interface SplitDraft {
    /** Stabiler Schluessel der Teilzeile in der Oberflaeche. */
    key: string;
    invoiceId: string;
    /** Betrag, wie getippt ("999,60"). */
    amountInput: string;
    remainder: RemainderKind | null;
}

export interface SplitEvaluation {
    /** Buchbare Teile — nur gesetzt, wenn es keinen Fehler gibt. */
    parts: SplitPart[] | null;
    sumCents: Cents;
    /** Umsatz minus Summe der Teile; 0 heisst: vollstaendig verteilt. */
    restCents: Cents;
    errors: string[];
    warnings: string[];
}

const hasMultipleTaxRates = (invoice: Invoice): boolean =>
    invoice.totals.taxGroups.filter((group) => group.taxableNetCents !== 0).length > 1;

export const evaluateSplit = (
    transaction: BankTransaction,
    drafts: readonly SplitDraft[],
    invoicesById: ReadonlyMap<string, Invoice>,
    todayIso: string,
): SplitEvaluation => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const parts: SplitPart[] = [];
    let sumCents = 0;

    if (drafts.length < 2) errors.push('Zum Aufteilen mindestens zwei Rechnungen wählen.');

    const seen = new Set<string>();
    drafts.forEach((draft, index) => {
        const label = `Teil ${index + 1}`;
        const invoice = draft.invoiceId ? invoicesById.get(draft.invoiceId) : undefined;
        const amount = centsFromEuroInput(draft.amountInput);
        if (amount != null) sumCents += amount;
        if (!invoice) {
            errors.push(`${label}: keine Rechnung gewählt.`);
            return;
        }
        if (seen.has(invoice.id)) {
            errors.push(`${label}: ${invoiceName(invoice)} steht schon in einem anderen Teil.`);
            return;
        }
        seen.add(invoice.id);
        if (amount == null || amount <= 0) {
            errors.push(`${label}: der Betrag ist nicht lesbar oder nicht größer als 0,00 €.`);
            return;
        }
        if (hasMultipleTaxRates(invoice)) {
            errors.push(
                `${label}: ${invoiceName(invoice)} enthält mehrere Steuersätze — die Aufteilung in Netto und Umsatzsteuer muss an der Rechnung erfasst werden.`,
            );
            return;
        }
        const draftPayment = {
            direction: 'INCOMING' as const,
            valueDate: transaction.valueDate,
            amountCents: amount,
            method: 'BANK_TRANSFER' as const,
        };
        for (const message of validatePayment(draftPayment, invoice, todayIso)) {
            (message.level === 'error' ? errors : warnings).push(`${label}: ${message.message}`);
        }
        let remainder: SplitPart['remainder'];
        if (draft.remainder) {
            const rest = paymentRemainder(invoice, amount);
            if (rest <= 0) {
                errors.push(`${label}: es bleibt kein Rest, der ausgeglichen werden könnte.`);
            } else {
                for (const message of checkRemainder(invoice, draft.remainder, rest, transaction.valueDate, amount)) {
                    (message.level === 'error' ? errors : warnings).push(`${label}: ${message.message}`);
                }
                remainder = { kind: draft.remainder, amountCents: rest };
            }
        }
        parts.push({ invoice, amountCents: amount, remainder });
    });

    const restCents = transaction.amountCents - sumCents;
    if (drafts.length >= 2 && restCents !== 0) {
        errors.push(
            restCents > 0
                ? `Noch ${formatCents(restCents)} zu verteilen.`
                : `Die Teile ergeben ${formatCents(-restCents)} mehr als der Umsatz.`,
        );
    }

    return {
        parts: errors.length === 0 ? parts : null,
        sumCents,
        restCents,
        errors,
        warnings,
    };
};

/**
 * Vorbelegung einer Aufteilung: die vorgeschlagenen Rechnungen der Reihe
 * nach mit ihrem offenen Betrag, bis der Umsatz verteilt ist. Mindestens zwei
 * Zeilen — eine leere, wenn nur eine Rechnung vorgeschlagen ist.
 */
export const initialSplitDrafts = (
    transaction: BankTransaction,
    suggested: readonly Invoice[],
    makeKey: () => string,
    formatAmount: (cents: Cents) => string,
): SplitDraft[] => {
    const drafts: SplitDraft[] = [];
    let remaining = transaction.amountCents;
    for (const invoice of suggested) {
        // Ist der Umsatz verteilt, kommen keine weiteren Vorschlaege dazu —
        // eine Zeile ohne Betrag machte die Aufteilung sonst ungueltig.
        if (remaining <= 0 && drafts.length >= 2) break;
        if (drafts.some((draft) => draft.invoiceId === invoice.id)) continue;
        const amount = Math.max(0, Math.min(openAmountCents(invoice), remaining));
        // Ohne verteilbaren Betrag kein Vorschlag — eine Zeile ohne Betrag
        // machte die Aufteilung ungueltig (Codex Runde 7).
        if (amount <= 0) continue;
        remaining -= amount;
        drafts.push({
            key: makeKey(),
            invoiceId: invoice.id,
            amountInput: formatAmount(amount),
            remainder: null,
        });
    }
    // Auffuellen auf zwei Zeilen: der noch nicht verteilte Rest steht in der
    // ersten Leerzeile — auch wenn es gar keinen verteilbaren Vorschlag gab.
    while (drafts.length < 2) {
        drafts.push({
            key: makeKey(),
            invoiceId: '',
            amountInput: remaining > 0 ? formatAmount(remaining) : '',
            remainder: null,
        });
        remaining = 0;
    }
    return drafts;
};
